import React, {
  createContext,
  useReducer,
  Dispatch,
  ReactNode,
  useMemo,
  useEffect
} from 'react'
import { getMenu } from '../lib/CfgApi/getMenu'
import { useAmpUserStatus } from '../lib/hooks/useAmpUserStatus'
import { UserStatus } from '../types/UserStatus.enum'
import { UserService, UserResponse } from '@ampcore/client-side-services'
import { fetchVisitorInfoData } from '../lib/VisitorInfo/fetchVisitorInfoData'
import {
  AppState,
  ActionType,
  Action,
  initialState,
  appReducer
} from './AppContext.types'

export const AppContext = createContext<{
  state: AppState
  dispatch: Dispatch<Action>
}>({
  state: initialState,
  dispatch: () => {
    throw new Error('dispatch function must be used within AppProvider')
  }
})

interface AppContextProviderProps {
  children: ReactNode
  menuItemsUrl?: string
  menuVersion?: string
  isMenuFetchDisabled?: boolean
  restrictToProspectDefault?: boolean
}

export const AppContextProvider = ({
  children,
  menuItemsUrl = '/cfgapi/hst/menus/ampcom',
  menuVersion = '1.0.0',
  isMenuFetchDisabled = false,
  restrictToProspectDefault = false
}: AppContextProviderProps) => {
  const [state, dispatch] = useReducer(appReducer, initialState)
  const ampUserStatus = useAmpUserStatus()

  // Set ampUserStatus in context
  useEffect(() => {
    if (restrictToProspectDefault) {
      dispatch({
        type: ActionType.SET_AMP_USER_STATUS,
        payload: UserStatus.PROSPECT
      })
      return
    }

    if (ampUserStatus) {
      dispatch({ type: ActionType.SET_AMP_USER_STATUS, payload: ampUserStatus })
    }
  }, [ampUserStatus])

  // Set menuName based on ampUserStatus, but only if it changes
  useEffect(() => {
    if (restrictToProspectDefault) {
      dispatch({
        type: ActionType.SET_MENU_NAME,
        payload: 'main-menu-prospect'
      })
      return
    }

    if (
      ampUserStatus === UserStatus.CLIENT_ACTIVE ||
      ampUserStatus === UserStatus.CLIENT_EXPIRED ||
      ampUserStatus === UserStatus.PROSPECT
    ) {
      let menuName: string | null = null
      if (ampUserStatus === UserStatus.CLIENT_ACTIVE) {
        menuName = 'main-menu-client-active'
      } else {
        menuName = 'main-menu-prospect'
      }
      if (menuName !== state.menuName) {
        dispatch({ type: ActionType.SET_MENU_NAME, payload: menuName })
      }
    }
    // Do not dispatch if ampUserStatus is not yet determined
  }, [ampUserStatus, state.menuName, restrictToProspectDefault])

  // Fetch menu items when menuName changes
  useEffect(() => {
    const fetchMenu = async () => {
      if (!state.menuName) {
        dispatch({ type: ActionType.SET_MENU_ITEMS, payload: [] })
        return
      }

      if (isMenuFetchDisabled) {
        return
      }

      dispatch({ type: ActionType.SET_MENU_LOADING, payload: true })
      try {
        const items = await getMenu(
          menuItemsUrl,
          state.menuName,
          3,
          menuVersion
        )
        dispatch({ type: ActionType.SET_MENU_ITEMS, payload: items })
      } catch {
        dispatch({
          type: ActionType.SET_MENU_ERROR,
          payload: 'Failed to fetch menu data'
        })
      } finally {
        dispatch({ type: ActionType.SET_MENU_LOADING, payload: false })
      }
    }
    fetchMenu()
  }, [state.menuName, menuItemsUrl, menuVersion, isMenuFetchDisabled])

  // Fetch userData when ampUserStatus is CLIENT_ACTIVE
  useEffect(() => {
    if (restrictToProspectDefault) {
      dispatch({ type: ActionType.SET_USER_DATA, payload: null })
      return
    }

    if (ampUserStatus === UserStatus.CLIENT_ACTIVE) {
      const userService = new UserService()
      userService
        .get()
        .then((response: UserResponse) => {
          dispatch({ type: ActionType.SET_USER_DATA, payload: response })
        })
        .catch(() => {
          dispatch({ type: ActionType.SET_USER_DATA, payload: null })
        })
    } else {
      dispatch({ type: ActionType.SET_USER_DATA, payload: null })
    }
  }, [ampUserStatus, restrictToProspectDefault])

  // Fetch advisorData when ampUserStatus is PROSPECT
  useEffect(() => {
    const fetchAdvisor = async () => {
      if (restrictToProspectDefault) {
        dispatch({ type: ActionType.SET_ADVISOR_DATA, payload: null })
        return
      }

      if (ampUserStatus === UserStatus.PROSPECT) {
        dispatch({ type: ActionType.SET_ADVISOR_DATA_LOADING, payload: true })
        try {
          const result = await fetchVisitorInfoData()
          const data =
            result?.visitor?.targetingData?.advisorcollector?.extraData
              ?.attributes
          dispatch({
            type: ActionType.SET_ADVISOR_DATA,
            payload: {
              image: data?.image,
              name: data?.name,
              phonenumber: data?.phonenumber,
              tagline: data?.tagline,
              url: data?.url,
              email: data?.email
            }
          })
        } catch {
          dispatch({
            type: ActionType.SET_ADVISOR_DATA_ERROR,
            payload: 'Failed to fetch advisor data'
          })
        }
      } else {
        dispatch({ type: ActionType.SET_ADVISOR_DATA, payload: null })
      }
    }
    fetchAdvisor()
  }, [ampUserStatus, restrictToProspectDefault])

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch])
  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  )
}
